import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  addHealComment,
  deleteHealDecision,
  getHeal,
  listHealComments,
  patchHealStatus,
  setHealAssignee,
  upsertHealDecision,
  type HealComment,
  type HealDecision,
  type HealMatchKind,
  type HealProposalSlim,
  type RunHealDecisionKind,
  type RunHealDetail,
  type RunHealStatus,
} from "../lib/api";
import { useAuth } from "../lib/auth";
import { HealStatusBadge } from "./HealsPage";
import { formatRelativeTime } from "./TracesPage";
import { HealPrDialog } from "../components/HealPrDialog";
import { HealLinearDialog } from "../components/HealLinearDialog";
import { HealJiraDialog } from "../components/HealJiraDialog";

/**
 * Heal review — /heals/:id
 *
 * Groups proposals by matchKind (exact / soft / missing) and lets the user
 * accept / reject / override each one. Decisions POST to the API; the
 * server recomputes `status` and returns it inline so the badge updates
 * without a second round-trip.
 *
 * When every proposal has a decision, status flips to `accepted` and the
 * "Send as PR" button unlocks (wiring lives in Task #133 — for now the
 * button is disabled with a "coming soon" title). A "Discard" button is
 * always available and drops the heal into a terminal state.
 *
 * The page is read-only for heals already in a terminal state (pr-opened,
 * merged, discarded): decision buttons render disabled so an approved
 * heal isn't silently mutated.
 */
export function HealReviewPage(): JSX.Element {
  const { id = "" } = useParams();
  const { entitlements } = useAuth();
  const [heal, setHeal] = useState<RunHealDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mutating, setMutating] = useState<Set<string>>(new Set());
  const [statusBusy, setStatusBusy] = useState(false);
  const [prOpen, setPrOpen] = useState(false);
  const [linearOpen, setLinearOpen] = useState(false);
  const [jiraOpen, setJiraOpen] = useState(false);

  // Plan §21 Phase 4 tier-gate: hide the Linear/Jira buttons when the
  // user's plan doesn't include the channel. Server-side 402 still
  // applies defensively. `entitlements` is null while /api/auth/me is
  // loading — be conservative and hide until we know.
  const linearAllowed = entitlements?.integrations?.includes("linear") ?? false;
  const jiraAllowed = entitlements?.integrations?.includes("jira") ?? false;

  const load = useCallback(async () => {
    setError(null);
    try {
      const fresh = await getHeal(id);
      setHeal(fresh);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    setHeal(null);
    setError(null);
    (async () => {
      try {
        const h = await getHeal(id);
        if (!cancelled) setHeal(h);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // previousUaipId → decision, for O(1) lookup inside the proposal rows.
  const decisionsByProposal = useMemo(() => {
    const map = new Map<string, HealDecision>();
    if (heal) {
      for (const d of heal.decisions) {
        map.set(d.previousUaipId, d);
      }
    }
    return map;
  }, [heal]);

  const proposals = useMemo<HealProposalSlim[]>(() => {
    const raw = heal?.manifest?.proposals;
    if (!Array.isArray(raw)) return [];
    return raw as HealProposalSlim[];
  }, [heal]);

  const buckets = useMemo(() => bucketProposals(proposals), [proposals]);

  const terminal =
    heal !== null &&
    (heal.status === "pr-opened" ||
      heal.status === "merged" ||
      heal.status === "discarded");

  const fullyDecided =
    heal !== null && heal.totalCount > 0 && heal.decisions.length >= heal.totalCount;

  async function applyDecision(
    proposal: HealProposalSlim,
    decision: RunHealDecisionKind,
    opts: { newUaipId?: string | null; note?: string | null } = {},
  ): Promise<void> {
    if (!heal) return;
    const key = proposal.previousUaipId;
    setMutating((m) => new Set(m).add(key));
    try {
      const res = await upsertHealDecision(heal.id, {
        previousUaipId: proposal.previousUaipId,
        decision,
        ...(opts.newUaipId !== undefined ? { newUaipId: opts.newUaipId } : {}),
        ...(opts.note !== undefined ? { note: opts.note } : {}),
      });
      // Merge the returned decision locally — saves another round-trip.
      setHeal((prev) => {
        if (!prev) return prev;
        const next = prev.decisions.filter(
          (d) => d.previousUaipId !== proposal.previousUaipId,
        );
        next.push(res.decision);
        return {
          ...prev,
          decisions: next,
          status: res.healStatus,
        };
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setMutating((m) => {
        const next = new Set(m);
        next.delete(key);
        return next;
      });
    }
  }

  async function clearDecision(proposal: HealProposalSlim): Promise<void> {
    if (!heal) return;
    const key = proposal.previousUaipId;
    setMutating((m) => new Set(m).add(key));
    try {
      await deleteHealDecision(heal.id, proposal.previousUaipId);
      // Refresh from server — status auto-transition logic lives server-side,
      // and reloading is simpler than duplicating it here.
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setMutating((m) => {
        const next = new Set(m);
        next.delete(key);
        return next;
      });
    }
  }

  async function setStatus(status: RunHealStatus): Promise<void> {
    if (!heal) return;
    setStatusBusy(true);
    try {
      const fresh = await patchHealStatus(heal.id, status);
      setHeal((prev) => (prev ? { ...prev, status: fresh.status } : prev));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setStatusBusy(false);
    }
  }

  if (error) {
    return (
      <div>
        <BackLink />
        <div className="mt-4 rounded-md border border-rose-700 bg-rose-900/40 px-4 py-3 text-sm text-rose-200">
          {error}
        </div>
      </div>
    );
  }

  if (!heal) {
    return (
      <div>
        <BackLink />
        <div className="mt-4 rounded-lg border border-slate-800 bg-slate-900/40 p-8 text-center text-slate-400">
          Loading heal report…
        </div>
      </div>
    );
  }

  return (
    <div>
      <BackLink />

      <header className="mt-4 mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold">Heal report</h1>
            <HealStatusBadge status={heal.status} />
          </div>
          <p className="mt-1 text-sm text-slate-400">
            <span className="font-mono text-xs">{heal.id}</span>
            {" · "}
            {heal.source}
            {" · "}
            created {formatRelativeTime(heal.createdAt)}
          </p>
          {(heal.previousCrawlId || heal.newCrawlId || heal.runId) && (
            <p className="mt-1 text-xs text-slate-500">
              {heal.previousCrawlId && (
                <>
                  previous crawl{" "}
                  <Link
                    to={`/crawls/${encodeURIComponent(heal.previousCrawlId)}`}
                    className="font-mono text-indigo-300 hover:text-indigo-200"
                  >
                    {heal.previousCrawlId}
                  </Link>
                </>
              )}
              {heal.previousCrawlId && heal.newCrawlId && " → "}
              {heal.newCrawlId && (
                <>
                  new crawl{" "}
                  <Link
                    to={`/crawls/${encodeURIComponent(heal.newCrawlId)}`}
                    className="font-mono text-indigo-300 hover:text-indigo-200"
                  >
                    {heal.newCrawlId}
                  </Link>
                </>
              )}
              {heal.runId && (
                <>
                  {(heal.previousCrawlId || heal.newCrawlId) && " · "}
                  run{" "}
                  <Link
                    to={`/runs/${encodeURIComponent(heal.runId)}/trace`}
                    className="font-mono text-indigo-300 hover:text-indigo-200"
                  >
                    {heal.runId}
                  </Link>
                </>
              )}
            </p>
          )}
        </div>

        <div className="flex flex-col items-end gap-2">
          <div className="text-xs text-slate-400">
            {heal.decisions.length} of {heal.totalCount} decided
          </div>
          <div className="flex items-center gap-2">
            {heal.prUrl ? (
              <a
                href={heal.prUrl}
                target="_blank"
                rel="noopener"
                className="rounded-md border border-indigo-500 bg-indigo-600/30 px-3 py-1.5 text-sm text-indigo-100 hover:bg-indigo-600/50"
              >
                View PR →
              </a>
            ) : (
              <button
                type="button"
                onClick={() => setPrOpen(true)}
                disabled={!fullyDecided || terminal || statusBusy}
                className="rounded-md border border-indigo-500 bg-indigo-600/30 px-3 py-1.5 text-sm text-indigo-100 hover:bg-indigo-600/50 disabled:cursor-not-allowed disabled:opacity-50"
                title={
                  terminal
                    ? "Heal is in a terminal state"
                    : fullyDecided
                      ? "Open a GitHub PR with the accepted selector changes"
                      : "Decide every proposal first"
                }
              >
                Send as PR
              </button>
            )}
            {/* Send as Linear issue — independent of GitHub PR. Heal can be
                filed in either or both; Linear doesn't gate on terminal
                status because it just creates a tracking issue.
                Hidden on Free/Starter — server still 402s defensively. */}
            {linearAllowed && (
              <button
                type="button"
                onClick={() => setLinearOpen(true)}
                disabled={statusBusy}
                className="rounded-md border border-purple-500 bg-purple-600/30 px-3 py-1.5 text-sm text-purple-100 hover:bg-purple-600/50 disabled:cursor-not-allowed disabled:opacity-50"
                title="File this heal as a Linear issue"
              >
                Send as Linear issue
              </button>
            )}
            {jiraAllowed && (
              <button
                type="button"
                onClick={() => setJiraOpen(true)}
                disabled={statusBusy}
                className="rounded-md border border-sky-500 bg-sky-600/30 px-3 py-1.5 text-sm text-sky-100 hover:bg-sky-600/50 disabled:cursor-not-allowed disabled:opacity-50"
                title="File this heal as a Jira issue"
              >
                Send as Jira issue
              </button>
            )}
            {!terminal && (
              <button
                type="button"
                onClick={() => setStatus("discarded")}
                disabled={statusBusy}
                className="rounded-md border border-rose-700 bg-rose-900/40 px-3 py-1.5 text-sm text-rose-200 hover:bg-rose-900/60 disabled:opacity-50"
              >
                {statusBusy ? "…" : "Discard"}
              </button>
            )}
          </div>
          {fullyDecided && !terminal && !heal.prUrl && (
            <div className="text-xs text-emerald-400">
              All proposals decided. Ready to open a PR.
            </div>
          )}
        </div>
      </header>

      <SummaryCards heal={heal} />

      {/* Team workflow (assignee + comments) is a Team/Enterprise
       *  feature (Plan §21 Phase 4). Hidden on Free/Starter/Pro so the
       *  upgrade path is obvious — server still 402's defensively on
       *  /api/heals/:id/{assignee,comments}. */}
      {entitlements?.teamCollaboration && (
        <CollabPanel heal={heal} onHealChanged={(next) => setHeal(next)} />
      )}

      {terminal && (
        <div className="mb-4 rounded-md border border-slate-700 bg-slate-900/60 px-4 py-3 text-sm text-slate-300">
          This heal is in a terminal state (<code className="font-mono">{heal.status}</code>).
          Decisions are read-only. Create a new heal to propose further changes.
        </div>
      )}

      {proposals.length === 0 && (
        <div className="mt-8 rounded-lg border border-dashed border-slate-800 bg-slate-900/40 p-8 text-center text-slate-400">
          No proposals in this heal — the previous and current crawls already
          agree on every element. Nothing to do.
        </div>
      )}

      {buckets.soft.length > 0 && (
        <BucketSection
          title="Needs review"
          subtitle="Soft matches — similar but not identical. Check the mapping before accepting."
          matchKind="soft"
          proposals={buckets.soft}
          decisions={decisionsByProposal}
          mutating={mutating}
          disabled={terminal}
          onApply={applyDecision}
          onClear={clearDecision}
        />
      )}

      {buckets.missing.length > 0 && (
        <BucketSection
          title="Gone"
          subtitle="Elements that no longer exist in the new crawl. Accept to delete the selector from your tests, or override to point it somewhere else."
          matchKind="missing"
          proposals={buckets.missing}
          decisions={decisionsByProposal}
          mutating={mutating}
          disabled={terminal}
          onApply={applyDecision}
          onClear={clearDecision}
        />
      )}

      {buckets.exact.length > 0 && (
        <BucketSection
          title="Exact matches"
          subtitle="Fingerprint hashes agree — safe to accept in bulk."
          matchKind="exact"
          proposals={buckets.exact}
          decisions={decisionsByProposal}
          mutating={mutating}
          disabled={terminal}
          onApply={applyDecision}
          onClear={clearDecision}
        />
      )}

      {prOpen && (
        <HealPrDialog
          heal={heal}
          onClose={() => setPrOpen(false)}
          onOpened={() => {
            // Server flipped status → pr-opened and stamped prUrl. Refresh
            // so the badge + "View PR" link render against the persisted
            // row rather than optimistic local state.
            void load();
          }}
        />
      )}
      {linearOpen && (
        <HealLinearDialog
          heal={heal}
          onClose={() => setLinearOpen(false)}
        />
      )}
      {jiraOpen && (
        <HealJiraDialog
          heal={heal}
          onClose={() => setJiraOpen(false)}
        />
      )}
    </div>
  );
}

function BackLink(): JSX.Element {
  return (
    <Link
      to="/heals"
      className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-200"
    >
      ← All heals
    </Link>
  );
}

function SummaryCards({ heal }: { heal: RunHealDetail }): JSX.Element {
  const cards: { label: string; value: string; tone: string }[] = [
    {
      label: "Exact",
      value: String(heal.exactCount),
      tone: "text-emerald-300",
    },
    {
      label: "Soft",
      value: String(heal.softCount),
      tone: "text-amber-300",
    },
    {
      label: "Missing",
      value: String(heal.missingCount),
      tone: "text-rose-300",
    },
    {
      label: "Mean soft confidence",
      value: heal.softCount === 0 ? "—" : heal.meanSoftConfidence.toFixed(2),
      tone: "text-slate-200",
    },
  ];
  return (
    <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
      {cards.map((c) => (
        <div
          key={c.label}
          className="rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-3"
        >
          <div className="text-xs uppercase tracking-wider text-slate-500">
            {c.label}
          </div>
          <div className={`mt-1 text-xl font-semibold tabular-nums ${c.tone}`}>
            {c.value}
          </div>
        </div>
      ))}
    </div>
  );
}

interface BucketSectionProps {
  title: string;
  subtitle: string;
  matchKind: HealMatchKind;
  proposals: HealProposalSlim[];
  decisions: Map<string, HealDecision>;
  mutating: Set<string>;
  disabled: boolean;
  onApply: (
    proposal: HealProposalSlim,
    decision: RunHealDecisionKind,
    opts?: { newUaipId?: string | null; note?: string | null },
  ) => Promise<void>;
  onClear: (proposal: HealProposalSlim) => Promise<void>;
}

function BucketSection(props: BucketSectionProps): JSX.Element {
  const { title, subtitle, matchKind, proposals, decisions, mutating, disabled, onApply, onClear } =
    props;
  return (
    <section className="mb-8">
      <header className="mb-2">
        <h2 className="text-lg font-semibold text-slate-100">
          {title}{" "}
          <span className="ml-1 text-sm font-normal text-slate-500">
            ({proposals.length})
          </span>
        </h2>
        <p className="text-xs text-slate-500">{subtitle}</p>
      </header>
      <div className="divide-y divide-slate-800 overflow-hidden rounded-lg border border-slate-800">
        {proposals.map((p) => (
          <ProposalRow
            key={p.previousUaipId}
            proposal={p}
            matchKind={matchKind}
            decision={decisions.get(p.previousUaipId) ?? null}
            busy={mutating.has(p.previousUaipId)}
            disabled={disabled}
            onApply={onApply}
            onClear={onClear}
          />
        ))}
      </div>
    </section>
  );
}

interface ProposalRowProps {
  proposal: HealProposalSlim;
  matchKind: HealMatchKind;
  decision: HealDecision | null;
  busy: boolean;
  disabled: boolean;
  onApply: (
    proposal: HealProposalSlim,
    decision: RunHealDecisionKind,
    opts?: { newUaipId?: string | null; note?: string | null },
  ) => Promise<void>;
  onClear: (proposal: HealProposalSlim) => Promise<void>;
}

function ProposalRow(props: ProposalRowProps): JSX.Element {
  const { proposal, matchKind, decision, busy, disabled, onApply, onClear } = props;
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideValue, setOverrideValue] = useState(proposal.newUaipId ?? "");
  const [note, setNote] = useState(decision?.note ?? "");

  // Reset the override form when the decision is cleared externally.
  useEffect(() => {
    if (!decision || decision.decision !== "override") {
      setOverrideValue(proposal.newUaipId ?? "");
    } else if (decision.newUaipId) {
      setOverrideValue(decision.newUaipId);
    }
    setNote(decision?.note ?? "");
  }, [decision, proposal.newUaipId]);

  const effective = disabled || busy;

  return (
    <div className="bg-slate-950/30 px-4 py-3">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-mono text-xs text-slate-300">
              {proposal.previousUaipId}
            </span>
            <MatchBadge matchKind={matchKind} confidence={proposal.matchConfidence} />
          </div>
          <div className="mt-1 text-sm text-slate-200">
            {renderFromTo(proposal)}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            {proposal.matchReason}
          </div>
          {proposal.previousPageUrl && (
            <div className="mt-1 text-xs text-slate-600">
              on{" "}
              <span className="font-mono">
                {proposal.previousPageUrl}
                {proposal.newPageUrl && proposal.newPageUrl !== proposal.previousPageUrl
                  ? ` → ${proposal.newPageUrl}`
                  : ""}
              </span>
            </div>
          )}
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1.5">
          {decision ? (
            <DecisionPill decision={decision.decision} />
          ) : (
            <span className="text-xs text-slate-500">undecided</span>
          )}
          <div className="flex items-center gap-1.5">
            <DecisionButton
              label="Accept"
              tone="emerald"
              active={decision?.decision === "accept"}
              disabled={effective}
              onClick={() => onApply(proposal, "accept", { note: note.trim() || null })}
            />
            <DecisionButton
              label="Reject"
              tone="rose"
              active={decision?.decision === "reject"}
              disabled={effective}
              onClick={() => onApply(proposal, "reject", { note: note.trim() || null })}
            />
            <DecisionButton
              label="Override"
              tone="indigo"
              active={decision?.decision === "override"}
              disabled={effective}
              onClick={() => setOverrideOpen((v) => !v)}
            />
            {decision && !disabled && (
              <button
                type="button"
                onClick={() => onClear(proposal)}
                disabled={busy}
                className="rounded px-2 py-1 text-xs text-slate-500 hover:text-slate-200 disabled:opacity-50"
                title="Clear decision"
              >
                ✕
              </button>
            )}
          </div>
        </div>
      </div>

      {overrideOpen && !disabled && (
        <div className="mt-3 rounded-md border border-indigo-800 bg-indigo-900/20 p-3">
          <label className="block text-xs font-medium text-indigo-200">
            New uaipId
          </label>
          <input
            type="text"
            className="mt-1 w-full rounded border border-slate-700 bg-slate-900 px-2 py-1 font-mono text-xs text-slate-100 focus:border-indigo-500 focus:outline-none"
            placeholder="element-id-in-new-crawl"
            value={overrideValue}
            onChange={(e) => setOverrideValue(e.target.value)}
          />
          <label className="mt-2 block text-xs font-medium text-indigo-200">
            Note (optional)
          </label>
          <input
            type="text"
            className="mt-1 w-full rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-100 focus:border-indigo-500 focus:outline-none"
            placeholder="why this override?"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={2000}
          />
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setOverrideOpen(false)}
              className="rounded px-2 py-1 text-xs text-slate-400 hover:text-slate-200"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!overrideValue.trim() || busy}
              onClick={async () => {
                await onApply(proposal, "override", {
                  newUaipId: overrideValue.trim(),
                  note: note.trim() || null,
                });
                setOverrideOpen(false);
              }}
              className="rounded-md border border-indigo-500 bg-indigo-600/40 px-3 py-1 text-xs text-indigo-100 hover:bg-indigo-600/60 disabled:opacity-50"
            >
              Save override
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function renderFromTo(proposal: HealProposalSlim): JSX.Element {
  const fromName = proposal.previousAccessibleName ?? "<unnamed>";
  const toName = proposal.newAccessibleName;
  const toId = proposal.newUaipId;
  if (proposal.matchKind === "missing" || !toId) {
    return (
      <span>
        <span className="text-slate-300">“{fromName}”</span>
        <span className="text-slate-500"> → </span>
        <span className="text-rose-300">not found</span>
      </span>
    );
  }
  return (
    <span>
      <span className="text-slate-300">“{fromName}”</span>
      <span className="text-slate-500"> → </span>
      <span className="text-emerald-300">“{toName ?? "<unnamed>"}”</span>
      <span className="ml-1 font-mono text-xs text-slate-500">
        ({toId})
      </span>
    </span>
  );
}

function MatchBadge({
  matchKind,
  confidence,
}: {
  matchKind: HealMatchKind;
  confidence: number;
}): JSX.Element {
  const cls =
    matchKind === "exact"
      ? "border-emerald-700 bg-emerald-900/30 text-emerald-300"
      : matchKind === "soft"
        ? "border-amber-700 bg-amber-900/30 text-amber-300"
        : "border-rose-700 bg-rose-900/30 text-rose-300";
  const label =
    matchKind === "exact"
      ? "exact"
      : matchKind === "soft"
        ? `soft ${confidence.toFixed(2)}`
        : "missing";
  return (
    <span className={`rounded border px-1.5 py-0.5 text-[10px] font-medium ${cls}`}>
      {label}
    </span>
  );
}

function DecisionPill({
  decision,
}: {
  decision: RunHealDecisionKind;
}): JSX.Element {
  const cls =
    decision === "accept"
      ? "bg-emerald-900/40 text-emerald-200 border-emerald-700"
      : decision === "reject"
        ? "bg-rose-900/40 text-rose-200 border-rose-700"
        : "bg-indigo-900/40 text-indigo-200 border-indigo-700";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${cls}`}
    >
      {decision}
    </span>
  );
}

function DecisionButton(props: {
  label: string;
  tone: "emerald" | "rose" | "indigo";
  active: boolean;
  disabled: boolean;
  onClick: () => void;
}): JSX.Element {
  const toneClass =
    props.tone === "emerald"
      ? props.active
        ? "border-emerald-500 bg-emerald-600/40 text-emerald-100"
        : "border-slate-700 bg-slate-900 text-slate-200 hover:border-emerald-600 hover:text-emerald-200"
      : props.tone === "rose"
        ? props.active
          ? "border-rose-500 bg-rose-600/40 text-rose-100"
          : "border-slate-700 bg-slate-900 text-slate-200 hover:border-rose-600 hover:text-rose-200"
        : props.active
          ? "border-indigo-500 bg-indigo-600/40 text-indigo-100"
          : "border-slate-700 bg-slate-900 text-slate-200 hover:border-indigo-600 hover:text-indigo-200";
  return (
    <button
      type="button"
      disabled={props.disabled}
      onClick={props.onClick}
      className={`rounded-md border px-2 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${toneClass}`}
    >
      {props.label}
    </button>
  );
}

function bucketProposals(proposals: HealProposalSlim[]): {
  exact: HealProposalSlim[];
  soft: HealProposalSlim[];
  missing: HealProposalSlim[];
} {
  const exact: HealProposalSlim[] = [];
  const soft: HealProposalSlim[] = [];
  const missing: HealProposalSlim[] = [];
  for (const p of proposals) {
    if (p.matchKind === "exact") exact.push(p);
    else if (p.matchKind === "soft") soft.push(p);
    else missing.push(p);
  }
  return { exact, soft, missing };
}

/**
 * Plan §21 Phase 4 team workflow v1 — assignee + comments thread.
 *
 * v1 scope is intentionally minimal:
 *   - Assignee is a self-assign / unassign toggle (current user only).
 *     A v2 picker would let Team-tier users pick teammates from the
 *     project's collaborators.
 *   - Comments are append-only plain text (no edit/delete) so the audit
 *     trail stays clean.
 */
function CollabPanel(props: {
  heal: RunHealDetail;
  onHealChanged: (heal: RunHealDetail) => void;
}): JSX.Element {
  const { user } = useAuth();
  const [comments, setComments] = useState<HealComment[] | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await listHealComments(props.heal.id);
        if (!cancelled) setComments(list);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [props.heal.id]);

  const isAssignedToMe =
    user !== null && props.heal.assigneeUserId === user.id;
  const hasAnyAssignee = props.heal.assigneeUserId !== null;

  async function handleAssignToggle(): Promise<void> {
    if (!user) return;
    setBusy(true);
    setError(null);
    try {
      const next = isAssignedToMe ? null : user.id;
      const updated = await setHealAssignee(props.heal.id, next);
      // Merge the new assigneeUserId back into the parent's heal state.
      props.onHealChanged({ ...props.heal, assigneeUserId: updated.assigneeUserId });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function handlePostComment(
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> {
    e.preventDefault();
    if (!draft.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const c = await addHealComment(props.heal.id, draft.trim());
      setComments((prev) => [...(prev ?? []), c]);
      setDraft("");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mt-4 rounded-xl border border-slate-700 bg-slate-900/50 p-4">
      <header className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-200">Review &amp; notes</h2>
        <div className="flex items-center gap-2 text-xs">
          {hasAnyAssignee ? (
            <span className="rounded-full border border-emerald-700/60 bg-emerald-900/30 px-2 py-0.5 text-emerald-200">
              Assigned to{" "}
              {isAssignedToMe ? "you" : (
                <span className="font-mono">{props.heal.assigneeUserId}</span>
              )}
            </span>
          ) : (
            <span className="rounded-full border border-slate-700 bg-slate-800 px-2 py-0.5 text-slate-400">
              Unassigned
            </span>
          )}
          {user && (
            <button
              type="button"
              onClick={handleAssignToggle}
              disabled={busy}
              className="rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800 disabled:opacity-50"
            >
              {isAssignedToMe ? "Unassign me" : "Assign to me"}
            </button>
          )}
        </div>
      </header>

      {error && (
        <div className="mb-3 rounded-md border border-rose-700/60 bg-rose-900/30 px-3 py-2 text-xs text-rose-200">
          {error}
        </div>
      )}

      <ul className="mb-3 flex flex-col gap-2">
        {comments === null && (
          <li className="text-xs text-slate-500">Loading comments…</li>
        )}
        {comments !== null && comments.length === 0 && (
          <li className="text-xs text-slate-500">
            No notes yet. Use the box below to leave the first one.
          </li>
        )}
        {comments?.map((c) => (
          <li
            key={c.id}
            className="rounded-md border border-slate-800 bg-slate-950/40 px-3 py-2 text-sm text-slate-200"
          >
            <div className="mb-1 text-xs text-slate-500">
              <span className="font-mono">
                {c.authorUserId
                  ? c.authorUserId === user?.id
                    ? "you"
                    : c.authorUserId
                  : "(removed user)"}
              </span>{" "}
              · {new Date(c.createdAt).toLocaleString()}
            </div>
            <div className="whitespace-pre-wrap">{c.body}</div>
          </li>
        ))}
      </ul>

      {user ? (
        <form onSubmit={handlePostComment} className="flex flex-col gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            placeholder="Add a note for the next reviewer…"
            className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
            maxLength={4000}
          />
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={busy || !draft.trim()}
              className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? "Posting…" : "Post note"}
            </button>
          </div>
        </form>
      ) : (
        <p className="text-xs text-slate-500">Sign in to post a note.</p>
      )}
    </section>
  );
}

